import { useMemo, useState } from 'react';
import { contestants, categories, type Contestant } from '@/mocks/chigua';

function fmt(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)}万` : `${n}`;
}

export default function Ranking() {
  const [active, setActive] = useState('全部');
  const [voted, setVoted] = useState<Record<number, boolean>>({});
  const [votes, setVotes] = useState<Record<number, number>>({});

  const list = useMemo(() => {
    const filtered = active === '全部' ? contestants : contestants.filter((c) => c.category === active);
    return filtered.map((c) => ({ ...c, votes: c.votes + (votes[c.id] || 0) }));
  }, [active, votes]);

  const maxVotes = Math.max(...list.map((c) => c.votes));

  const vote = (id: number) => {
    setVoted((p) => ({ ...p, [id]: true }));
    setVotes((p) => ({ ...p, [id]: (p[id] || 0) + 1 }));
  };

  const rankIcon = (i: number) => {
    if (i === 0) return <i className="ri-medal-fill text-sun-400"></i>;
    if (i === 1) return <i className="ri-medal-fill text-slate-400"></i>;
    if (i === 2) return <i className="ri-medal-fill text-orange-400"></i>;
    return null;
  };

  return (
    <section id="ranking" className="w-full bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest text-melon-500 uppercase">
              <i className="ri-fire-fill"></i> Live Ranking
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl md:text-5xl text-ink">
              实时人气排行榜
            </h2>
            <p className="mt-3 text-sm md:text-base text-ink/60 max-w-xl">
              票数实时滚动更新，为你支持的选手投票即可拉动排名上升。点击卡片右侧按钮免费打榜。
            </p>
          </div>
          <div className="flex items-center gap-1.5 text-xs md:text-sm text-leaf-700 bg-leaf-50 rounded-full px-3 py-1.5 w-fit">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-leaf-500 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-leaf-500"></span></span>
            已累计投票 <b className="font-display">{fmt(36270000)}</b>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActive(cat)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                active === cat
                  ? 'bg-melon-500 text-white shadow-sm'
                  : 'bg-melon-50 text-melon-600 hover:bg-melon-100'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {list.map((c, idx) => (
            <article
              key={c.id}
              className="rounded-2xl border border-melon-100 bg-canvas p-5 hover:border-melon-200 hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="relative">
                  <img src={c.avatar} alt={`${c.name}的选手头像`} className="w-16 h-16 rounded-xl object-cover" />
                  <span className={`absolute -bottom-2 -right-2 w-7 h-7 rounded-full flex items-center justify-center text-base bg-white shadow-sm ${rankIcon(idx) ? 'text-xl' : 'text-ink/50'}`}>
                    {rankIcon(idx) || idx + 1}
                  </span>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {c.momentum === 'up' && <span className="text-xs font-semibold text-melon-500 bg-melon-50 rounded-full px-2 py-0.5"><i className="ri-arrow-up-line"></i>{c.trend.toFixed(1)}%</span>}
                  {c.momentum === 'new' && <span className="text-xs font-semibold text-leaf-700 bg-leaf-50 rounded-full px-2 py-0.5">新晋黑马</span>}
                  {c.momentum === 'steady' && <span className="text-xs font-semibold text-sun-500 bg-sun-50 rounded-full px-2 py-0.5">稳步上升</span>}
                  <span className="text-[11px] text-ink/50">{c.category}</span>
                </div>
              </div>

              <h3 className="mt-4 font-display font-bold text-lg text-ink">{c.name}</h3>
              <p className="mt-0.5 text-sm text-ink/60">{c.tagline}</p>

              <div className="mt-4">
                <div className="flex items-baseline justify-between text-xs text-ink/50 mb-1.5">
                  <span>人气值</span>
                  <span className="font-display font-bold text-melon-600 text-base">{fmt(c.votes)}</span>
                </div>
                <div className="h-2 rounded-full bg-melon-100 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-melon-400 to-melon-600" style={{ width: `${(c.votes / maxVotes) * 100}%` }}></div>
                </div>
              </div>

              <button
                onClick={() => vote(c.id)}
                disabled={!!voted[c.id]}
                className={`mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                  voted[c.id]
                    ? 'bg-leaf-100 text-leaf-700'
                    : 'bg-gradient-to-r from-melon-500 to-melon-600 text-white hover:from-melon-600 hover:to-melon-700'
                }`}
              >
                {voted[c.id] ? <><i className="ri-check-double-line"></i>已助力 · 谢谢支持</> : <><i className="ri-hand-heart-line"></i>投一票</>}
              </button>
            </article>
          ))}
        </div>

        <p className="mt-6 text-center text-xs md:text-sm text-ink/40">
          榜单实时刷新 · 更多选手见参赛看点专区 · 每人每天可多次免费打榜
        </p>
      </div>
    </section>
  );
}